"use client";

import React, { useState, useMemo, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { classNames } from "@/lib/utils";
import { enabledLocales } from "@/constants";
interface LanguageDropdownProps {
  className?: string;
  isSticky?: boolean;
  variant?: "light" | "dark";
}

// All available languages with their metadata
const ALL_LANGUAGES = [{
  name: "English",
  code: "en",
  icon: <svg className="h-5 w-5 rounded-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <g fillRule="evenodd">
                    <g strokeWidth="1pt">
                        <path fill="#bd3d44" d="M0 0h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0z" transform="scale(3.9385)" />
                        <path fill="#fff" d="M0 10h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0z" transform="scale(3.9385)" />
                    </g>
                    <path fill="#192f5d" d="M0 0h98.8v70H0z" transform="scale(3.9385)" />
                    <path fill="#fff" d="M8.2 3l1 2.8H12L9.7 7.5l.9 2.7-2.4-1.7L6 10.2l.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8H45l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7L74 8.5l-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9L92 7.5l1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9z" transform="scale(3.9385)" />
                </g>
            </svg>
}, {
  name: "Español",
  code: "es",
  icon: <svg className="h-5 w-5 rounded-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <g fillRule="evenodd" strokeWidth="1pt">
                    <path fill="#c60b1e" d="M0 0h512v512H0z" />
                    <path fill="#ffc400" d="M0 170.7h512v170.6H0z" />
                </g>
            </svg>
}, {
  name: "Français",
  code: "fr",
  icon: <svg className="h-5 w-5 rounded-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <g fillRule="evenodd" strokeWidth="1pt">
                    <path fill="#002395" d="M0 0h170.7v512H0z" />
                    <path fill="#fff" d="M170.7 0h170.6v512H170.7z" />
                    <path fill="#ed2939" d="M341.3 0H512v512H341.3z" />
                </g>
            </svg>
}, {
  name: "Deutsch",
  code: "de",
  icon: <svg className="h-5 w-5 rounded-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path fill="#ffce00" d="M0 341.3h512V512H0z" />
                <path d="M0 0h512v170.7H0z" />
                <path fill="#d00" d="M0 170.7h512v170.6H0z" />
            </svg>
}, {
  name: "Português",
  code: "pt",
  icon: <svg className="h-5 w-5 rounded-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path fill="#006600" d="M0 0h512v512H0z" />
                <path fill="#ff0000" d="M204.8 0H512v512H204.8z" />
                <circle cx="204.8" cy="256" r="102.4" fill="#ffcc00" />
            </svg>
}];
export default function LanguageDropdown({
  className,
  isSticky,
  variant = "light"
}: LanguageDropdownProps) {
  const isDark = variant === "dark";
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const toggleDropdown = () => setIsOpen(!isOpen);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Extract locale from pathname (more reliable than useLocale hook)
  const currentLocale = useMemo(() => {
    const pathSegments = pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];
    if (firstSegment && enabledLocales.includes(firstSegment)) {
      return firstSegment;
    }
    return "en";
  }, [pathname]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter languages to only show enabled locales
  const languages = useMemo(() => ALL_LANGUAGES.filter(lang => enabledLocales.includes(lang.code)), []);
  const currentLanguage = languages.find(lang_0 => lang_0.code === currentLocale) || languages[0];

  // Handle language change with cookie + full page reload
  const handleLanguageChange = (languageCode: string) => {
    setIsOpen(false);

    // Set locale preference cookie BEFORE navigation
    // This tells the middleware to respect the user's explicit choice
    document.cookie = `NEXT_LOCALE=${languageCode}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;

    // Extract path without locale prefix
    const pathSegments_0 = pathname.split("/").filter(Boolean);
    const firstSegment_0 = pathSegments_0[0];
    let pathWithoutLocale: string;
    if (firstSegment_0 && enabledLocales.includes(firstSegment_0)) {
      pathWithoutLocale = "/" + pathSegments_0.slice(1).join("/") || "/";
    } else {
      pathWithoutLocale = pathname;
    }

    // Build new URL - English has no prefix, other locales have /{locale} prefix
    const localeUrl = languageCode === "en" ? pathWithoutLocale : `/${languageCode}${pathWithoutLocale === "/" ? "" : pathWithoutLocale}`;

    // Force full page reload to trigger middleware
    window.location.href = localeUrl;
  };
  return <div ref={dropdownRef} className={classNames(isSticky ? "sticky bottom-0 z-10" : "relative", isDark ? "inline-flex" : "hidden md:inline-flex", className)}>
            {/* Trigger Button - Premium borderless design */}
            <button onClick={toggleDropdown} type="button" aria-expanded={isOpen} aria-haspopup="listbox" className={classNames("group inline-flex items-center gap-2.5 border border-white/10 px-3 py-2 transition-colors duration-75 focus:outline-none", isDark ? "rounded-xl text-white/70 hover:bg-white/[0.06] hover:text-white" : "border bg-white text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 dark:border-gray-700 dark:bg-slate-900 dark:text-gray-400 dark:hover:bg-slate-800", !isDark && (isSticky ? "rounded-tr-lg" : "rounded-lg"))}>
                {/* Flag with subtle ring */}
                <span className={classNames("flex-shrink-0 overflow-hidden rounded-full", isDark && "ring-1 ring-white/20")}>{currentLanguage?.icon}</span>
                {/* Language name */}
                <span className="text-[14px] font-medium">{currentLanguage?.name}</span>
                {/* Chevron */}
                <svg className={classNames("h-3.5 w-3.5 transition-transform duration-100", isDark ? "text-white/40 group-hover:text-white/60" : "text-gray-500", isOpen && "rotate-180")} viewBox="0 0 16 16" fill="none">
                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {/* Dropdown Menu - Premium glass design */}
            <div role="listbox" aria-label="Select language" className={classNames("absolute z-50 origin-bottom transition-all duration-200 ease-out", isDark ? "bottom-full left-0 mb-3 min-w-[200px] rounded-2xl bg-[#1c1c1c]/95 py-2 shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.05)] backdrop-blur-xl" : "bottom-[50px] rounded-xl border bg-white shadow-lg", isOpen ? "pointer-events-auto translate-y-0 scale-100 opacity-100" : "pointer-events-none translate-y-2 scale-95 opacity-0")}>
                {languages.map((language, index) => {
        const isActive = language.code === currentLocale;
        return <button type="button" title={language.name} key={index} role="option" aria-selected={isActive} onClick={() => handleLanguageChange(language.code)} className={classNames("group mx-2 flex w-[calc(100%-16px)] items-center gap-3 rounded-xl px-3 py-2.5 transition-colors duration-75", isDark ? classNames(isActive ? "bg-white/[0.08] text-white" : "text-white/70 hover:bg-white/[0.04] hover:text-white") : classNames("text-gray-700 hover:bg-gray-50", isActive && "bg-gray-50 font-medium"))}>
                            {/* Flag icon with subtle ring on dark */}
                            <span className={classNames("flex-shrink-0 overflow-hidden rounded-full", isDark && "ring-1 ring-white/10 group-hover:ring-white/20", isActive && isDark && "ring-white/25")}>{language.icon}</span>
                            {/* Language name */}
                            <span className={classNames("flex-1 text-left text-[14px]", isActive ? "font-medium" : "font-normal")}>{language.name}</span>
                            {/* Check indicator for active language */}
                            {isActive && isDark && <svg className="h-4 w-4 text-white/60" viewBox="0 0 16 16" fill="none">
                                    <path d="M13.5 4.5L6.5 11.5L2.5 7.5" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                                </svg>}
                        </button>;
      })}
            </div>
        </div>;
}